import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import * as internationalReturnsUpsellImage from "@redotech/redo-web/international-returns-upsell.png?url";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { OnboardingProps } from "../common";

export const InternationalReturnsOnboarding = memo(
  function InternationalReturnsOnboarding({
    handleDismiss,
    handleComplete,
  }: OnboardingProps) {
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const [modalOpen, setModalOpen] = useState(true);
    const [successModalOpen, setSuccessModalOpen] = useState(false);

    const handleJoinWaitlist = useHandler(async () => {
      setModalOpen(false);

      try {
        await rpcClient.sendInternationalReturnsWaitlistNotification(null);
      } catch (error) {
        console.error("Failed to send waitlist notification:", error);
      }

      setSuccessModalOpen(true);
    });

    const handleSecondaryAction = useHandler(() => {
      setModalOpen(false);
      handleDismiss();
    });

    const handleModalClose = useHandler(() => {
      setModalOpen(false);
      handleDismiss();
    });

    const handleSuccessModalClose = useHandler(() => {
      setSuccessModalOpen(false);
      handleComplete();
    });

    return (
      <>
        {/* Success Modal */}
        <RedoModal
          isOpen={successModalOpen}
          modalSize="sm"
          onModalCloseRequested={handleSuccessModalClose}
          primaryButton={{
            text: "Close",
            onClick: handleSuccessModalClose,
            size: "sm",
          }}
          subtitle="Thanks for signing up — you're officially on the waitlist for international returns. We will be in touch soon."
          title="You're on the list."
        />

        {/* Main Onboarding Modal */}
        <RedoModal
          hideHeader
          isOpen={modalOpen}
          modalSize="md"
          noPadding
          onModalCloseRequested={handleModalClose}
          primaryButton={{
            text: "Join waitlist",
            onClick: handleJoinWaitlist,
            size: "sm",
          }}
          secondaryButton={{
            text: "Dismiss",
            onClick: handleSecondaryAction,
            size: "sm",
          }}
          title=""
        >
          <div>
            <img
              alt="International Returns"
              src={internationalReturnsUpsellImage.default}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
            <Flex dir="column" gap="xl" style={{ padding: "24px" }}>
              <Text fontSize="xl" fontWeight="semibold">
                Simplify your international returns
              </Text>
              <Text fontSize="sm" textColor="secondary">
                With Redo International Returns you can now offer a free returns
                experience to customers in the UK, Canada, and Australia.
              </Text>

              <Flex dir="column" gap="lg">
                <Text fontSize="md" fontWeight="medium">
                  Here is what's included:
                </Text>

                <Flex dir="column" gap="sm">
                  <Text fontSize="sm" textColor="secondary">
                    &bull;{" "}
                    <Text as="span" fontWeight="semibold">
                      Local carriers & drop off points
                    </Text>{" "}
                    for easy access for shoppers to send items back
                  </Text>
                  <Text fontSize="sm" textColor="secondary">
                    &bull;{" "}
                    <Text as="span" fontWeight="semibold">
                      Redo handles the complexity
                    </Text>{" "}
                    of commercial invoices to avoid duties and taxes
                  </Text>
                  <Text fontSize="sm" textColor="secondary">
                    &bull;{" "}
                    <Text as="span" fontWeight="semibold">
                      Returns are processed within days not weeks,
                    </Text>{" "}
                    thanks to Redo's in-country returns centers
                  </Text>
                  <Text fontSize="sm" textColor="secondary">
                    &bull;{" "}
                    <Text as="span" fontWeight="semibold">
                      Similar prices to a US domestic return
                    </Text>{" "}
                    and{" "}
                    <Text as="span" fontStyle="italic">
                      way
                    </Text>{" "}
                    less than cross-border labels.
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          </div>
        </RedoModal>
      </>
    );
  },
);
